
import { MatchingPair } from '../../utilities/GameUtils.js';
import { BaseTheme } from '../abstract/BaseTheme.js';
import { PositionStrategy } from './PositionStrategy.js';
import { ShapePositions } from "../../utilities/GameUtils.js";

/**
 * Circular positioning strategy for sequential themes.
 * Draggables arranged in a circle, all targets in the center (requires isSequential=true).
 */
export class CircularPositionStrategy extends PositionStrategy {

    /**
     * Calculate vertical positions for shapes.
     * @param width - Canvas width
     * @param height - Canvas height
     * @param numPairs - Number of shape pairs
     * @param shapeSize - Size of the shapes 
     * @returns Positions with DS in circle, TS in center
     */
    calculatePositions(width: number, height: number, matchingPairs: MatchingPair[], _shapeSize: number, _isSequential: boolean): ShapePositions {
        const centerX = width / 2;
        const centerY = height / 2;

        const numPairs = matchingPairs.length;
        
        // Calculate radius for the circle - leave space for shape size and padding
        const maxRadius = Math.min(width, height) / 2 - 5 * 6;
        const radius = maxRadius * 0.75; // Use 75% of max radius for better spacing
        
        const ds: { x: number; y: number }[] = [];
        const ts: { x: number; y: number }[] = [];
        
        // Calculate angle step for evenly distributed points
        const angleStep = (2 * Math.PI) / numPairs;
        const startAngle = -Math.PI / 2; // Start from top
        
        for (let i = 0; i < numPairs; i++) {
            const angle = startAngle + (i * angleStep);
            
            // Place draggables in a circle
            ds.push({
                x: centerX + radius * Math.cos(angle),
                y: centerY + radius * Math.sin(angle)
            });
            
            // Place all targets in the center
            ts.push({
                x: centerX,
                y: centerY
            });
        }
        
        return { DS_POSITIONS: ds, TS_POSITIONS: ts };
    }

    // circular strategy only works with sequential themes
    isThemeValid(theme: BaseTheme): boolean {
        if (!theme.isSequential) {
            throw new Error(`CircularPositionStrategy can only be used with sequential themes, isSequential is ${theme.isSequential}, Theme: ${theme.constructor.name}.`);
        }
        return true;
    }

    // custom, a bit larger for better visibility
    getShapeSize(height: number, width: number, _numPairs: number): number {
        const isHorizontal = width > height;
        let ss = Math.floor( (isHorizontal ? height : width) / (5 * 1.1)); // Base size on available width divided by number of pairs with some spacing
        return Math.max(60, ss );
    }
}
